import {
  scaleLinear,
} from 'd3-scale';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import sortBy from 'lodash-es/sortBy';
import {
  ProductClass,
} from '../../graphQL/types/shared';
import {
  ILoadable,
  LoadableStatus,
} from '../../Utils';
import {
  getIntervalWidener,
  IntervalWidener,
} from '../../viz/Utils';
import {
  ILayoutData,
} from '../fetchedData/layoutData';
import {
  IMetadatum,
} from '../fetchedData/productMetadata';
import {
  IAssignSectionOutput,
  INodeWithoutTrade,
  ISize,
  xDomainWideningFactor,
  yDomainWideningFactor,
} from './Utils';

export const getAssign = (
    // This allows for injection during testing:
    widenIntervalX: IntervalWidener = getIntervalWidener(xDomainWideningFactor),
    widenIntervalY: IntervalWidener = getIntervalWidener(yDomainWideningFactor),
  ) => (
  sizeStatus: ILoadable<ISize>,
  layoutDataStatus: ILoadable<ILayoutData>,
  levelMappingStatus: ILoadable<Record<string, number>>,
  metadataStatus: ILoadable<Record<string, IMetadatum>>,
  colorMap: Map<number, string>,
  productClass: ProductClass): ILoadable<IAssignSectionOutput> => {

  let result: ILoadable<IAssignSectionOutput>;
  if (layoutDataStatus.status === LoadableStatus.Initial ||
      levelMappingStatus.status === LoadableStatus.Initial ||
      metadataStatus.status === LoadableStatus.Initial ||
      sizeStatus.status === LoadableStatus.Initial) {

    result = {status: LoadableStatus.Initial};
  } else if (layoutDataStatus.status === LoadableStatus.Loading ||
      levelMappingStatus.status === LoadableStatus.Loading ||
      metadataStatus.status === LoadableStatus.Loading ||
      sizeStatus.status === LoadableStatus.Loading) {

    result = {status: LoadableStatus.Loading};
  } else if (layoutDataStatus.status === LoadableStatus.NotPresent ||
      levelMappingStatus.status === LoadableStatus.NotPresent ||
      metadataStatus.status === LoadableStatus.NotPresent ||
      sizeStatus.status === LoadableStatus.NotPresent) {

    result = {status: LoadableStatus.NotPresent};
  } else {
    const {data: {nodes, edges}} = layoutDataStatus;
    const {data: levelMapping} = levelMappingStatus;
    const {data: metadata} = metadataStatus;

    // Sort by IDs to avoid unnecessary DOM unmounting/remounting when node
    // sizing is set to "None":
    const sortedNodes = sortBy(nodes, ({id}) => id);
    // Figure out the on-screen coords of nodes:
    const {data: {width, height}} = sizeStatus;
    const allXs = sortedNodes.map(({x}) => x);
    const allYs = sortedNodes.map(({y}) => y);
    const minX = min(allXs)!;
    const minY = min(allYs)!;
    const maxX = max(allXs)!;
    const maxY = max(allYs)!;
    // Give the chart some padding:
    const widenedXDomain = widenIntervalX(minX, maxX);
    const widenedYDomain = widenIntervalY(minY, maxY);

    const xScale = scaleLinear<number, number>()
                    .domain(widenedXDomain)
                    .range([0, width]);
    const yScale = scaleLinear<number, number>()
                    .domain(widenedYDomain)
                    .range([0, height]);
    const nodeList: INodeWithoutTrade[] = sortedNodes.map(node => {
      const {id} = node;
      const section = levelMapping[id];
      const color = colorMap.get(section);
      if (color === undefined) {
        throw new Error('Cannot find color for section ' + section);
      }
      const retrievedMetadatum = metadata[id];
      if (retrievedMetadatum === undefined) {
        throw new Error('Cannot find metadata for product ' + id);
      }
      const {name_short_en, name_en, code, level} = retrievedMetadatum;
      return {
        ...node,
        uniqueKey: `${productClass}-${id}`,
        color, section, code, level,
        shortLabel: name_short_en,
        longLabel: name_en,
        scaledX: xScale(node.x),
        scaledY: yScale(node.y),
      };
    });

    result = {
      status: LoadableStatus.Present,
      data: {
        nodeList,
        edgeList: edges,
      },
    };
  }
  return result;

};

export default getAssign();
